import type { TvvStandaardRouteDto } from "@/shared/types/generated/dtos";
import {
    Organisatie,
    OrganisatieDisplay,
    Reisperiode,
    Routesoort,
    RoutesoortDisplay,
    Voertuigsoort,
    VoertuigsoortDisplay,
} from "../constants";
import { RouteForTitel } from "../types";
import { PeriodeFactor } from "./periode-factor";
import { Rijtijd } from "./rijtijd";

export type TvvStandaardRoute = {
    routeId: number;
    stationCodes: string[];
    organisatie: Organisatie;
    voertuigSoort: Voertuigsoort;
    routeSoort: Routesoort;
    opmerking?: string;
    voorwaarde?: string;
    periodeFactoren: PeriodeFactor[];
    rijtijden: Rijtijd[];
    totaleRijtijd: number | undefined;
    isOvRoute: boolean;
};

export type RouteTitelResult = {
    titel: string;
    isAfgekort: boolean;
};

type RouteStationNames = {
    beginStation: string;
    eindStation: string;
};

export function getRouteStationNames(stationCodes: string[], getStationName: (code: string) => string): RouteStationNames {
    return {
        beginStation: getStationName(stationCodes.at(0) ?? ""),
        eindStation: getStationName(stationCodes.at(-1) ?? ""),
    };
}

export const TvvStandaardRoute = {
    fromDto(dto: TvvStandaardRouteDto): TvvStandaardRoute {
        return {
            routeId: dto.routeId,
            stationCodes: dto.routeStationCodes,
            organisatie: dto.organisatie,
            voertuigSoort: dto.voertuigSoort,
            routeSoort: dto.routeSoort,
            opmerking: dto.opmerking,
            voorwaarde: dto.voorwaarde,
            periodeFactoren: dto.periodeFactoren.map((pf) => PeriodeFactor.fromDto(pf)),
            rijtijden: dto.rijtijden,
            totaleRijtijd: Rijtijd.getTotaleRijtijd(dto.rijtijden),
            isOvRoute: this.isOVRoute(dto.voertuigSoort, dto.organisatie),
        };
    },

    isOVRoute(voertuigSoort: Voertuigsoort, organisatie: Organisatie): boolean {
        return voertuigSoort.startsWith("OV") && ["GVB", "HTM", "RET", "U_OV"].includes(organisatie);
    },

    /** Nieuwe routes get a negative ID to distinguish them from server-side standaard routes */
    isNieuweRoute(routeId: number): boolean {
        return routeId < 0;
    },

    formatTitel(route: RouteForTitel, getStationName: (code: string) => string): string {
        const type = this.routeType(route);
        const stations = route.stationCodes.map(getStationName).join(" - ");
        return `${type} ${stations}`;
    },

    formatBeginEindStation(route: RouteForTitel, getStationName: (code: string) => string): string {
        const type = this.routeType(route);
        const stations = route.stationCodes.map(getStationName);
        return `${type} ${stations.at(0)} - ${stations.at(-1)}`;
    },

    formatOrganisatie(organisatie: Organisatie) {
        return OrganisatieDisplay[organisatie];
    },

    routeType(route: RouteForTitel) {
        if (route.isOvRoute) {
            return VoertuigsoortDisplay[route.voertuigSoort];
        }
        return RoutesoortDisplay[route.routeSoort];
    },

    formatKorteTitel(route: RouteForTitel, getStationName: (code: string) => string, maxTussenstations = 3): RouteTitelResult {
        const volledigeTitel = TvvStandaardRoute.formatTitel(route, getStationName);
        const tussenstations = route.stationCodes.length - 2;

        if (tussenstations <= maxTussenstations) {
            return { titel: volledigeTitel, isAfgekort: false };
        }

        const type = this.routeType(route);
        const beginStation = getStationName(route.stationCodes.at(0) ?? "");
        const eindStation = getStationName(route.stationCodes.at(-1) ?? "");

        return {
            titel: `${type} ${beginStation} - ${tussenstations} tussenstations - ${eindStation}`,
            isAfgekort: true,
        };
    },

    getPeriodeFactor({ periodeFactoren }: TvvStandaardRoute, periode: Reisperiode): PeriodeFactor | undefined {
        return periodeFactoren.find((pf) => pf.periode === periode);
    },

    sort(routes: TvvStandaardRoute[]): TvvStandaardRoute[] {
        return [...routes].sort((a, b) => {
            if (a.routeSoort !== b.routeSoort) {
                return a.routeSoort === Routesoort.Snel ? -1 : 1;
            }
            const firstA = a.stationCodes.at(0) ?? "";
            const firstB = b.stationCodes.at(0) ?? "";
            return firstA.localeCompare(firstB, "nl");
        });
    },
};
